import React, { useEffect, useState } from 'react'
import { EmissionDate, ReportingTable, PageContainer, DeleteModal, SiteSelect } from './component/Table'
import { columnDefinitionProcessEmission } from './component/Table/column'
import { useLocation, useNavigate } from 'react-router-dom'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import axios from 'axios'

const ReportProcessEmission = () => {
  const column = columnDefinitionProcessEmission
  const navigate = useNavigate()
  const location = useLocation()
  const { bypassModal } = location.state === null ? false : location.state
  const axiosPrivate = useAxiosPrivate()

  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showContentModal, setShowContentModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [modalMode, setModalMode] = useState(null) // 'add' or 'edit'
  const [editableData, setEditableData] = useState({
    emissionDate: '',
    siteId: '',
    title: '',
    co2EmissionFactor: '',
    ch4EmissionFactor: '',
    n20EmissionFactor: '',
    co2Emission: '',
    ch4Emission: '',
    n2oEmission: '',
    emission: '',
    details: '',
    reference: '',
  })
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [rowToDelete, setRowToDelete] = useState(null)

  useEffect(() => {
    let isMounted = true
    const controller = new AbortController()

    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await axiosPrivate.get(
          'processEmission/read-S1D-records',
          {
            signal: controller.signal,
          },
        )

        if (isMounted) {
          setData(response.data)
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Request failed:', err)
          if (err.response && err.response.status === 403) {
            navigate('/sign-in', { state: { from: location }, replace: true })
          }
        }
      }
      setIsLoading(false)
    }

    fetchData()

    if (bypassModal === true) {
      handleAddNewClick()
    }

    return () => {
      isMounted = false
      controller.abort()
    }
  }, [navigate, location, axiosPrivate])

  const handleAddNewClick = () => {
    setEditableData({
      emissionDate: '',
      siteId: '',
      title: '',
      co2EmissionFactor: '',
      ch4EmissionFactor: '',
      n20EmissionFactor: '',
      co2Emission: '',
      ch4Emission: '',
      n2oEmission: '',
      emission: '',
      details: '',
      reference: '',
    })
    setModalMode('add')
    setShowContentModal(true)
  }

  const handleDeleteClick = () => {
    if (selectedRows.size > 0) {
      setShowDeleteModal(true)
    } else {
      alert('No rows selected')
    }
  }

  const requestRowDeletion = (rowId) => {
    setRowToDelete(rowId)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (rowToDelete !== null) {
      let isMounted = true
      const controller = new AbortController()

      try {
        await axiosPrivate.delete(
          `/processEmission/delete-S1D-record/${rowToDelete}`,
          {
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
          },
        )

        if (isMounted) {
          setData((prevData) =>
            prevData.filter((row) => row.id !== rowToDelete),
          )
          setRowToDelete(null)
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Deletion failed:', err)
          if (err.response) {
            if (err.response.status === 403) {
              navigate('/sign-in', {
                state: { from: location },
                replace: true,
              })
            } else {
              alert(
                `Error: ${err.response.data.message || 'An error occurred.'}`,
              )
            }
          }
        }
      } finally {
        if (isMounted) {
          setShowDeleteModal(false)
        }
      }

      return () => {
        isMounted = false
        controller.abort()
      }
    } else {
      setShowDeleteModal(false)
    }
  }

  const deleteSelectedRows = async () => {
    if (selectedRows.size > 0) {
      let isMounted = true
      const controller = new AbortController()

      try {
        const recordIdsToDelete = Array.from(selectedRows)

        await axiosPrivate.delete('/processEmission/delete-S1D-records', {
          data: { ids: recordIdsToDelete },
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        })

        if (isMounted) {
          setData((prevData) =>
            prevData.filter((row) => !selectedRows.has(row.id)),
          )
          setSelectedRows(new Set())
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Deletion failed:', err)
          if (err.response) {
            if (err.response.status === 403) {
              navigate('/sign-in', {
                state: { from: location },
                replace: true,
              })
            } else {
              alert(
                `Error: ${err.response.data.message || 'An error occurred.'}`,
              )
            }
          }
        }
      } finally {
        if (isMounted) {
          setShowDeleteModal(false)
        }
      }

      return () => {
        isMounted = false
        controller.abort()
      }
    } else {
      setShowDeleteModal(false)
    }
  }

  const handleEditClick = (rowData) => {
    setEditableData(rowData)
    setModalMode('edit')
    setShowContentModal(true)
  }

  const handleAddNewSubmit = async (event) => {
    event.preventDefault()

    const endpoint =
      modalMode === 'edit'
        ? `/processEmission/edit-S1D-record/${editableData.id}`
        : '/processEmission/create-S1D-record'
    const method = modalMode === 'edit' ? 'patch' : 'post'

    const payload = {
      scope: 1,
      category: 'processEmission',
      emissionDate: editableData.emissionDate,
      siteId: editableData.siteId,
      title: editableData.title,
      co2EmissionFactor: editableData.co2EmissionFactor ? parseFloat(editableData.co2EmissionFactor) : 0,
      ch4EmissionFactor: editableData.ch4EmissionFactor ? parseFloat(editableData.ch4EmissionFactor) : 0,
      n20EmissionFactor: editableData.n20EmissionFactor ? parseFloat(editableData.n20EmissionFactor) : 0,
      co2Emission: parseFloat(editableData.co2Emission || 0),
      ch4Emission: parseFloat(editableData.ch4Emission || 0),
      n2oEmission: parseFloat(editableData.n2oEmission || 0),
      emission: parseFloat(editableData.emission || 0),
      details: editableData.details,
      reference: editableData.reference,
    }

    try {
      const response = await axiosPrivate[method](endpoint, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      if (modalMode === 'edit') {
        setData((currentData) =>
          currentData.map((item) =>
            item.id === editableData.id ? { ...item, ...response.data } : item,
          ),
        )
      } else {
        setData((currentData) => [...currentData, response.data])
      }

      setEditableData({
        emissionDate: '',
        siteId: '',
        title: '',
        co2EmissionFactor: '',
        ch4EmissionFactor: '',
        n20EmissionFactor: '',
        co2Emission: '',
        ch4Emission: '',
        n2oEmission: '',
        emission: '',
        details: '',
        reference: '',
      })
      setShowContentModal(false)
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error('Submission failed:', err)
        const errorMsg = err.response?.data.message || 'An error occurred.'
        if (err.response?.status === 403) {
          navigate('/sign-in', { state: { from: location }, replace: true })
        } else {
          alert(`Error: ${errorMsg}`)
        }
      }
    }
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <PageContainer
      title="Process Emission Reporting"
      onAdd={handleAddNewClick}
      onDelete={handleDeleteClick}
    >
      <ReportingTable
        data={data}
        columns={column}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        onRequestDelete={requestRowDeletion}
        onEdit={handleEditClick}
      />
      {showDeleteModal && (
        <DeleteModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={(row) => (row !== null ? confirmDelete() : deleteSelectedRows())}
          rowToDelete={rowToDelete}
        />
      )}
      {showContentModal && (
        <div className="fixed inset-0 bg-black border-rounded border bg-opacity-50 backdrop-blur-md flex justify-center items-center p-4 z-40">
          <div className="bg-white dark:bg-gray-800 w-full max-w-lg mx-auto rounded shadow-lg overflow-hidden p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {modalMode === 'edit' ? 'Edit Data' : 'Add New Data'}
            </h2>
            <form onSubmit={handleAddNewSubmit}>
              {/* Emission Date Input */}
              <EmissionDate
                data={editableData}
                setEditableData={setEditableData}
              />

              {/* Site Select */}
              <SiteSelect 
                value={editableData.siteId} 
                onChange={(val) => setEditableData({ ...editableData, siteId: val })} 
                required 
              />

              {/* Title Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Title
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.title}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      title: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Emission Factors */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                    CO2 Emission Factor
                  </label>
                  <input
                    type="number"
                    step="any"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.co2EmissionFactor}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        co2EmissionFactor: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                    CH4 Emission Factor
                  </label>
                  <input
                    type="number"
                    step="any"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.ch4EmissionFactor}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        ch4EmissionFactor: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                    N2O Emission Factor
                  </label>
                  <input
                    type="number"
                    step="any"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.n20EmissionFactor}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        n20EmissionFactor: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              {/* Emissions */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                    CO2 Emission (tCO2)
                  </label>
                  <input
                    type="number"
                    step="any"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.co2Emission}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        co2Emission: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                    CH4 Emission (tCH4)
                  </label>
                  <input
                    type="number"
                    step="any"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.ch4Emission}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        ch4Emission: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                    N2O Emission (tN2O)
                  </label>
                  <input
                    type="number"
                    step="any"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                    value={editableData.n2oEmission}
                    onChange={(e) =>
                      setEditableData({
                        ...editableData,
                        n2oEmission: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              </div>

              {/* Total CO2e Emission */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total CO2e Emission (Tonnes CO2e)
                </label>
                <input
                  type="number"
                  step="any"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.emission}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      emission: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Reference Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Reference
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.reference}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      reference: e.target.value,
                    })
                  }
                />
              </div>

              {/* Details / Description Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.details}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      details: e.target.value,
                    })
                  }
                />
              </div>

              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={() => setShowContentModal(false)}
                  className="bg-gray-300 dark:bg-gray-600 dark:text-gray-200 px-4 py-2 rounded mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageContainer>
  )
}

export default ReportProcessEmission
